import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star,
  Camera,
  Video,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Upload,
  Play,
  Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

// Max file sizes
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_VIDEO_DURATION = 30; // seconds

export default function PublicReview() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [photos, setPhotos] = useState([]); // Array of { file, preview, base64 }
  const [video, setVideo] = useState(null); // { file, preview, base64, duration }
  const [compressing, setCompressing] = useState(false);
  
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const videoPreviewRef = useRef(null);

  useEffect(() => {
    checkEligibility();
  }, [orderId, token]);

  const checkEligibility = async () => {
    if (!token) {
      setError("Lien invalide - token manquant");
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/public/reviews/check/${orderId}?token=${token}`);
      setEligibility(response.data);
      setCustomerName(response.data.customer_name || "");
      
      if (!response.data.can_review) {
        if (response.data.already_reviewed) {
          setError("Vous avez déjà laissé un avis pour cette commande");
        } else {
          setError("Cette commande n'est pas encore éligible pour un avis");
        }
      }
    } catch (err) {
      setError("Commande non trouvée ou lien invalide");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > 3) {
      toast.error("Maximum 3 photos autorisées");
      return;
    }
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error("Format non supporté. Utilisez JPG ou PNG");
        continue;
      }
      
      if (file.size > MAX_PHOTO_SIZE) {
        toast.error(`${file.name} est trop volumineux (max 10MB)`);
        continue;
      }
      
      // Compress and convert to base64
      setCompressing(true);
      try {
        const compressed = await compressImage(file);
        const base64 = await fileToBase64(compressed);
        setPhotos(prev => [...prev, {
          file: compressed,
          preview: URL.createObjectURL(compressed),
          base64
        }]);
      } catch (err) {
        toast.error("Erreur lors du traitement de l'image");
      }
      setCompressing(false);
    }
    
    e.target.value = '';
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      toast.error("Format non supporté. Utilisez MP4");
      return;
    }
    
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error("Vidéo trop volumineuse (max 50MB)");
      return;
    }
    
    // Check duration
    const duration = await getVideoDuration(file);
    if (duration > MAX_VIDEO_DURATION) {
      toast.error(`Vidéo trop longue (max ${MAX_VIDEO_DURATION} secondes)`);
      return;
    }
    
    setCompressing(true);
    try {
      // For simplicity, we'll just convert to base64 without re-encoding
      // Real compression would require ffmpeg.wasm or similar
      const base64 = await fileToBase64(file);
      setVideo({
        file,
        preview: URL.createObjectURL(file),
        base64,
        duration: Math.round(duration)
      });
    } catch (err) {
      toast.error("Erreur lors du traitement de la vidéo");
    }
    setCompressing(false);
    
    e.target.value = '';
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1200; // Max dimension
          let width = img.width;
          let height = img.height;
          
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.8);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  const removeVideo = () => {
    if (video) {
      URL.revokeObjectURL(video.preview);
      setVideo(null);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error("Veuillez donner une note");
      return;
    }
    
    if (!customerName.trim()) {
      toast.error("Veuillez entrer votre prénom");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/reviews/${orderId}?token=${token}`, {
        rating,
        comment: comment || null,
        customer_name: customerName,
        photos: photos.map(p => p.base64),
        video: video?.base64 || null
      });
      
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto mb-4" />
          <p className="text-[#888888]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-[#1B1B1B] mb-2">Oops !</h1>
          <p className="text-[#888888]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B1B1B] mb-3">Merci pour votre avis !</h1>
          <p className="text-[#888888] mb-6">
            Votre avis sera visible sur notre vitrine après validation.
            Nous apprécions votre retour d'expérience !
          </p>
          <div className="flex justify-center gap-1 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={star}
                className={`w-8 h-8 ${star <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`}
              />
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => window.close()}
            className="border-[#D4AF37] text-[#D4AF37]"
          >
            Fermer cette page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4 text-center">
          <h1 className="text-xl font-bold text-[#1B1B1B]">Partagez votre avis</h1>
          <p className="text-sm text-[#888888] mt-1">Votre retour nous aide à nous améliorer</p>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <form onSubmit={submitReview} className="space-y-6">
          {/* Order Info */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-[#888888] mb-2">Articles commandés :</p>
            {eligibility?.order_items?.map((item, idx) => (
              <p key={idx} className="text-sm font-medium text-[#1B1B1B]">
                {item.product_name} - {item.size} / {item.color}
              </p>
            ))}
          </div>

          {/* Rating */}
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <Label className="text-[#1B1B1B] font-medium block mb-4">
              Quelle note donnez-vous à votre expérience ?
            </Label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star 
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoverRating || rating) 
                        ? 'fill-[#D4AF37] text-[#D4AF37]' 
                        : 'text-gray-300 hover:text-[#D4AF37]/50'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-[#888888] mt-3">
              {rating === 0 && "Cliquez pour noter"}
              {rating === 1 && "Très déçue"}
              {rating === 2 && "Déçue"}
              {rating === 3 && "Correcte"}
              {rating === 4 && "Satisfaite"}
              {rating === 5 && "Excellente !"}
            </p>
          </div>

          {/* Comment */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
            <Label className="text-[#1B1B1B] font-medium">
              Votre commentaire (optionnel)
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre expérience avec cet article..."
              className="bg-[#F5F5F5] border-[#E5E5E5] rounded-xl min-h-[100px]"
              data-testid="review-comment"
            />
          </div>

          {/* Photos */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[#1B1B1B] font-medium">
                Photos (max 3)
              </Label>
              <span className="text-xs text-[#888888]">{photos.length}/3</span>
            </div>
            
            {/* Photo previews */}
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden group">
                    <img 
                      src={photo.preview} 
                      alt={`Photo ${idx + 1}`} 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add photo button */}
            {photos.length < 3 && (
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={compressing}
                className="w-full py-4 border-2 border-dashed border-[#E5E5E5] rounded-xl flex flex-col items-center gap-2 text-[#888888] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                {compressing ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Camera className="w-6 h-6" />
                )}
                <span className="text-sm">Ajouter une photo</span>
              </button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              multiple
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>

          {/* Video */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
            <Label className="text-[#1B1B1B] font-medium">
              Vidéo courte (max 30 sec)
            </Label>
            
            {/* Video preview */}
            {video && (
              <div className="relative rounded-xl overflow-hidden bg-black">
                <video
                  ref={videoPreviewRef}
                  src={video.preview}
                  className="w-full max-h-48 object-contain"
                  controls
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}s
                  </span>
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Add video button */}
            {!video && (
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={compressing}
                className="w-full py-4 border-2 border-dashed border-[#E5E5E5] rounded-xl flex flex-col items-center gap-2 text-[#888888] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                {compressing ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Video className="w-6 h-6" />
                )}
                <span className="text-sm">Ajouter une vidéo</span>
                <span className="text-xs">MP4 • Max 50MB • 30 sec</span>
              </button>
            )}
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/quicktime"
              onChange={handleVideoSelect}
              className="hidden"
            />
          </div>

          {/* Customer Name */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
            <Label className="text-[#1B1B1B] font-medium">
              Votre prénom (affiché publiquement)
            </Label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Marie"
              className="bg-[#F5F5F5] border-[#E5E5E5] rounded-xl"
              required
              data-testid="review-name"
            />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting || rating === 0}
            className="w-full bg-[#D4AF37] hover:bg-[#B8963C] text-white rounded-xl py-6 text-lg"
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Star className="w-5 h-5 mr-2" />
                Publier mon avis
              </>
            )}
          </Button>
        </form>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#F0F0F0] py-6 bg-white">
        <div className="max-w-lg mx-auto px-4 text-center">
          <p className="text-xs text-[#AAAAAA]">
            Votre avis sera vérifié avant publication
          </p>
        </div>
      </footer>
    </div>
  );
}
